import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../ClientForm/Form/reviewForm.css";
import {
  Button,
  Input,
  message,
  Modal,
  Radio,
  Select,
  Spin,
  Form,
  Row,
  Col,
} from "antd";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AiOutlineArrowLeft } from "react-icons/ai";
import { FaCalendarAlt } from "react-icons/fa";
import { hasPermission } from "../GlobalFunction";
import { setClientFormData, setClientFormStruc } from "../../Redux/ClientFormSlice";
import {
  UpdateClientRequest,
  getAllCountries, getClientAttachments,
  getProclozBillingEntityDropdown,
  addProclozBillingEntity,
  getCurrencyDropdown,
  getClientCountryFees,
  addClientCountryFee,
  updateClientCountryFee,
} from "../../services/ApiService";
import { NumericFormat } from "react-number-format";
import ClientCountryFees from "./ClientCountryFees";
import AccountOwnersByCountry from "./AccountOwnersByCountry";
const { Option } = Select;

const UpdateClientForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setloading] = useState(false);
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);

  // GitHub merged state variables
  const [countryIdOptions, setCountryIdOptions] = useState([]);
  const [proclozBillingEntities, setProclozBillingEntities] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [isProclozOthersSelected, setIsProclozOthersSelected] = useState(false);

  // Country-wise Fees: Country (form_fields.id 300) -- confirmed with
  // the user, 2026-10-16: reuses the Client_Onboard section's own fee
  // fields (client_deposit, client_setup_fees, ...) for a per-country
  // override instead of a separate Add Country button. Selecting a
  // country here temporarily swaps those fields to show (and, on
  // Submit Details, save) that country's existing override -- or blank,
  // ready to add one -- while countryFeeBaseSnapshotRef holds the
  // client's own real default values so they can be restored into the
  // actual client_form update payload (never sent as that country's
  // values) and so the fields go back to normal when the country is
  // cleared.
  const [countryFeeRows, setCountryFeeRows] = useState([]);
  const countryFeeBaseSnapshotRef = useRef(null);
  const COUNTRY_FEE_FIELD_NAME = "country_wise_fee_country_id";
  const COUNTRY_FEE_VALUE_FIELDS = [
    "client_deposit",
    "client_setup_fees",
    "client_termination_fees",
    "client_monthly_minimum_fees_locals",
    "client_monthly_minimum_fees_expats",
    "client_monthly_percentage",
    "client_year_end_charges",
    "vat_on_invoices",
    "vat_applied_on",
    "client_off_cycle_fees",
    "client_bank_charges",
    "client_it_procurement_charges",
    "client_adhoc_service_description",
    "recruitment_charges",
    "visa_sponsorship",
    "client_adhoc_service_charges",
  ];

  // File Attachments state variables
  const [isProofModalVisible, setIsProofModalVisible] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [originalPricingData, setOriginalPricingData] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isAttachmentsModalVisible, setIsAttachmentsModalVisible] = useState(false);

  const formData = useSelector((state) => state.Clientform.ClientformData);
  const formStructure = useSelector(
    (state) => state.Clientform.ClientformStructure
  );

  if (!formStructure || Object.keys(formStructure).length === 0) {
    navigate("/clientList");
  }

  useEffect(() => {
    const fetchCountryOptions = async () => {
      try {
        const response = await getAllCountries(1);
        const rawObjects = response?.country_list ?? [];

        const sortedRawObjects = [...rawObjects].sort((a, b) =>
          (a.country_name || "").localeCompare(b.country_name || "")
        );

        const countryOptions = sortedRawObjects.map((item) => {
          // item.country_name is something like "Afghanistan(AF)"
          const fullString = item.country_name || "";
          const cleanName = fullString.split("(")[0].trim();

          return {
            label: fullString,
            value: cleanName,
          };
        });

        const countryIdOptionsList = sortedRawObjects.map((item) => {
          const fullString = item.country_name || "";
          const cleanName = fullString.split("(")[0].trim();

          return {
            label: cleanName,
            value: item.id,
          };
        });
        setCountryIdOptions(countryIdOptionsList);

        const updatedStructure = JSON.parse(JSON.stringify(formStructure));

        Object.keys(updatedStructure).forEach((stepKey) => {
          updatedStructure[stepKey].forEach((section) => {
            Object.values(section).forEach((fields) => {
              fields.forEach((field) => {
                if (field.id === 131) {
                  field.option = countryOptions;
                }
              });
            });
          });
        });

        dispatch(setClientFormStruc(updatedStructure));
      } catch (error) {
        console.error("Error fetching country data:", error);
      }
    };

    fetchCountryOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchProclozBillingEntities = async () => {
      try {
        const response = await getProclozBillingEntityDropdown();
        setProclozBillingEntities(response?.procloz_billing_entities || []);
      } catch (error) {
        console.error("Error fetching Procloz billing entities:", error);
      }
    };

    fetchProclozBillingEntities();
  }, []);

  useEffect(() => {
    const fetchCurrencyOptions = async () => {
      try {
        const response = await getCurrencyDropdown();
        setCurrencyOptions(response?.currencies || []);
      } catch (error) {
        console.error("Error fetching currency dropdown:", error);
      }
    };

    fetchCurrencyOptions();
  }, []);

  const fetchCountryFeeRows = async () => {
    const client_id = sessionStorage.getItem("client_id");
    if (!client_id) return;
    try {
      const res = await getClientCountryFees(client_id);
      if (res?.responseCode === 200) {
        setCountryFeeRows(res.country_fees || []);
      }
    } catch (error) {
      console.error("Error fetching client country fees:", error);
    }
  };

  useEffect(() => {
    fetchCountryFeeRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFieldData = (payload) => {
    dispatch(setClientFormData(payload));
  };

  const handleFormChange = (value, name) => {
    setFieldData({ name, value });
    form.setFieldsValue({ [name]: value });
  };

  // Swaps the Client_Onboard section's own fee fields between the
  // client's real default values and the picked country's existing
  // override (or blank, if none yet) -- see COUNTRY_FEE_FIELD_NAME's
  // declaration above for why.
  useEffect(() => {
    const countryId = formData[COUNTRY_FEE_FIELD_NAME];

    if (countryId) {
      if (!countryFeeBaseSnapshotRef.current) {
        countryFeeBaseSnapshotRef.current = COUNTRY_FEE_VALUE_FIELDS.reduce(
          (acc, name) => ({ ...acc, [name]: formData[name] }),
          {}
        );
      }
      const existingRow = countryFeeRows.find((r) => r.country_id === countryId);
      COUNTRY_FEE_VALUE_FIELDS.forEach((name) => {
        handleFormChange(existingRow ? existingRow[name] || "" : "", name);
      });
    } else if (countryFeeBaseSnapshotRef.current) {
      COUNTRY_FEE_VALUE_FIELDS.forEach((name) => {
        handleFormChange(countryFeeBaseSnapshotRef.current[name] ?? "", name);
      });
      countryFeeBaseSnapshotRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData[COUNTRY_FEE_FIELD_NAME]]);

  useEffect(() => {
    const currentEntityValue = formData["procloz_billing_entity"];
    if (!currentEntityValue || proclozBillingEntities.length === 0) return;

    const matched = proclozBillingEntities.find(
      (entity) => String(entity.id) === String(currentEntityValue)
    );

    if (matched) {
      setIsProclozOthersSelected(false);
      if (formData["procloz_billing_entity_country_id"] !== matched.country_id) {
        handleFormChange(matched.country_id, "procloz_billing_entity_country_id");
      }
    } else {
      setIsProclozOthersSelected(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proclozBillingEntities, formData["procloz_billing_entity"]]);

  useEffect(() => {
    const currentEntityValue = formData["procloz_billing_entity"];
    if (!currentEntityValue || proclozBillingEntities.length === 0) return;

    const matched = proclozBillingEntities.find(
      (entity) => String(entity.id) === String(currentEntityValue)
    );

    if (matched) {
      setIsProclozOthersSelected(false);
      if (formData["procloz_billing_entity_country_id"] !== matched.country_id) {
        handleFormChange(matched.country_id, "procloz_billing_entity_country_id");
      }
    } else {
      setIsProclozOthersSelected(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proclozBillingEntities, formData["procloz_billing_entity"]]);

  const handleUpdate = async () => {
    try {
      setloading(true);

      const client_id = sessionStorage.getItem("client_id");
      let finalData = { ...formData, client_id };

      // If a country was picked in the Country-wise Fees section, save
      // those (possibly swapped-in) field values as THAT country's
      // override, then put the client's own real default values back
      // into finalData before the normal client_form update below --
      // confirmed with the user, 2026-10-16: never let a country's fees
      // overwrite the client's own base fees.
      const pendingCountryId = formData[COUNTRY_FEE_FIELD_NAME];
      if (pendingCountryId) {
        const feePayload = COUNTRY_FEE_VALUE_FIELDS.reduce(
          (acc, name) => ({ ...acc, [name]: formData[name] }),
          {}
        );
        const existingRow = countryFeeRows.find((r) => r.country_id === pendingCountryId);
        const countryFeeRes = existingRow
          ? await updateClientCountryFee(client_id, existingRow.id, feePayload)
          : await addClientCountryFee(client_id, { country_id: pendingCountryId, ...feePayload });

        if (countryFeeRes?.responseCode !== 200) {
          message.error(countryFeeRes?.responseMessage || "Failed to save the country fee.");
          setloading(false);
          return;
        }

        if (countryFeeBaseSnapshotRef.current) {
          COUNTRY_FEE_VALUE_FIELDS.forEach((name) => {
            finalData[name] = countryFeeBaseSnapshotRef.current[name];
          });
        }
        finalData[COUNTRY_FEE_FIELD_NAME] = null;
      }

      if (isProclozOthersSelected) {
        const entityRes = await addProclozBillingEntity({
          billing_entity: formData["procloz_billing_entity"],
          country_id: formData["procloz_billing_entity_country_id"],
        });
        const newEntity = entityRes?.data;
        if (!newEntity?.id) {
          message.error(
            entityRes?.responseMessage ||
              "Failed to create Procloz billing entity."
          );
          setloading(false);
          return;
        }
        finalData.procloz_billing_entity = newEntity.id;
        finalData.procloz_billing_entity_country_id = newEntity.country_id;
      }

      const formDataPayload = new FormData();
      formDataPayload.append('data', JSON.stringify(finalData));
      if (proofFile) formDataPayload.append('proof_file', proofFile);

      let res = await UpdateClientRequest(formDataPayload);
      setIsProofModalVisible(false);
      setProofFile(null);

      if (res.responseCode === 200) {
        message.success(res.responseMessage);
        navigate("/clientList");
      } else {
        message.error(res.responseMessage);
      }
    } catch (error) {
      console.error(error);
      message.error("Something went wrong. Please try again.");
    } finally {
      setloading(false);
    }
  };

  const handleSave = async () => {
    form
      .validateFields()
      .then(() => {
        const currentDataStr = JSON.stringify(formData);
        const originalDataStr = JSON.stringify(originalPricingData);
        
        if (currentDataStr !== originalDataStr) {
           setIsProofModalVisible(true);
           return;
        }

        Modal.confirm({
          title: "Are you sure?",
          content: "Do you want to update the information?",
          okText: "Yes, Update!",
          okType: "primary",
          cancelText: "Cancel",
          centered: true,
          onOk: () => form.submit(),
        });
      })
      .catch((errorInfo) => {
        message.error("Please fix validation errors before submitting.");
        const firstErrorField = errorInfo?.errorFields?.[0]?.name;
        if (firstErrorField) {
          form.scrollToField(firstErrorField, {
            behavior: "smooth",
            block: "center",
          });
        }
      });
  };

  const renderField = (fieldName, fieldData) => {
    const rules = [
      {
        required: fieldData.required,
        message: `${fieldData.label} is required`,
      },
    ];

    if (fieldData.input_type === "number") {
      rules.push({
        pattern: /^[0-9+\- ]+$/,
        message: "Only numbers, '+', '-' and spaces are allowed",
      });
    }

    if (fieldData.input_type === "alphabet") {
      rules.push({
        validator: (_, value) => {
          if (!value || /^[A-Za-z\s]+$/.test(value)) {
            return Promise.resolve();
          }
          return Promise.reject("Only alphabets are allowed");
        },
      });
    }

    const commonProps = {
      className: "clientinput-box",
      placeholder: fieldData.placeholder,
      disabled: !isEditing,
    };

    return (
      <Form.Item
        key={fieldName}
        name={fieldName}
        label={fieldData.label}
        rules={rules}
        // initialValue={formData[fieldName] || ""}
      >
        {fieldData.input_type === "text" ? (
          <Input
            {...commonProps}
            onChange={(e) => handleFormChange(e.target.value, fieldName)}
          />
        ) : fieldData.input_type === "number" ? (
          <Input
            {...commonProps}
            type="text"
            inputMode="numeric"
            pattern="[0-9+\- ]*"
            value={formData[fieldName] || ""}
            onChange={(e) => {
              const v = e.target.value;
              const cleaned = v.replace(/[^0-9+\- ]/g, "");
              handleFormChange(cleaned, fieldName);
            }}
          />
        ) : fieldData.input_type === "amount" ? (
          <NumericFormat
            className="input-box"
            placeholder={fieldData.placeholder}
            value={formData[fieldName] || ""}
            thousandSeparator={true}
            disabled={!isEditing}
            thousandsGroupStyle="lakh"
            decimalScale={2}
            customInput={Input}
            allowNegative={false}
            valueIsNumericString={true}
            type="text"
            onValueChange={(values) => {
              const raw = values.value;
              handleFormChange(raw, fieldName);
            }}
          />
        ) : fieldName === "icp_billing_currency" ? (
          <Select
            {...commonProps}
            showSearch
            allowClear
            onChange={(selectedValue) =>
              handleFormChange(selectedValue, fieldName)
            }
          >
            {currencyOptions.map((currency) => (
              <Option key={currency} value={currency}>
                {currency}
              </Option>
            ))}
          </Select>
        ) : fieldData.input_type === "alphabet" ? (
          <Input
            {...commonProps}
            onChange={(e) => {
              const onlyAlphabets = e.target.value.replace(/[^a-zA-Z\s]/g, "");
              handleFormChange(onlyAlphabets, fieldName);
            }}
          />
        ) : fieldName === "procloz_billing_entity" ? (
          <div className="procloz-billing-group">
            <Select
              className="procloz-country-select"
              disabled={!isEditing}
              showSearch
              allowClear
              placeholder="Country"
              value={formData["procloz_billing_entity_country_id"] || undefined}
              onChange={(value) =>
                handleFormChange(value, "procloz_billing_entity_country_id")
              }
              options={countryIdOptions}
              optionFilterProp="label"
            />
            {isProclozOthersSelected ? (
              <Input
                className="procloz-entity-control"
                disabled={!isEditing}
                placeholder="Enter Procloz Billing Entity"
                value={formData[fieldName] ?? ""}
                suffix={
                  <span
                    style={{
                      cursor: "pointer",
                      color: "red",
                      fontSize: "1rem",
                      marginRight: "4px",
                    }}
                    onClick={() => {
                      setIsProclozOthersSelected(false);
                      form.setFieldsValue({ [fieldName]: "" });
                      handleFormChange("", fieldName);
                    }}
                  >
                    x
                  </span>
                }
                onChange={(e) => handleFormChange(e.target.value, fieldName)}
              />
            ) : (
              <Select
                className="procloz-entity-control"
                disabled={!isEditing}
                showSearch
                allowClear
                placeholder={fieldData.placeholder}
                value={formData[fieldName] || undefined}
                onChange={(selectedValue) => {
                  if (selectedValue === "Other") {
                    setIsProclozOthersSelected(true);
                    form.setFieldsValue({ [fieldName]: "" });
                    handleFormChange("", fieldName);
                  } else {
                    handleFormChange(selectedValue, fieldName);
                    const matched = proclozBillingEntities.find(
                      (entity) => String(entity.id) === String(selectedValue)
                    );
                    if (matched) {
                      form.setFieldsValue({
                        procloz_billing_entity_country_id: matched.country_id,
                      });
                      handleFormChange(
                        matched.country_id,
                        "procloz_billing_entity_country_id"
                      );
                    }
                  }
                }}
              >
                {proclozBillingEntities.map((entity) => (
                  <Option key={entity.id} value={entity.id}>
                    {entity.billing_entity}
                  </Option>
                ))}
                <Option value="Other">Other</Option>
              </Select>
            )}
          </div>
        ) : fieldData.input_type === "select" ? (
          <Select
            {...commonProps}
            allowClear
            showSearch
            onChange={(selectedValue) =>
              handleFormChange(selectedValue, fieldName)
            }
          >
            {(fieldData.option || []).map((option, idx) =>
              typeof option === "string" ? (
                <Option key={idx} value={option}>
                  {option}
                </Option>
              ) : (
                <Option key={idx} value={option.value}>
                  {option.label}
                  {fieldName === COUNTRY_FEE_FIELD_NAME &&
                  countryFeeRows.some((r) => r.country_id === option.value)
                    ? " (already added)"
                    : ""}
                </Option>
              )
            )}
          </Select>
        ) : fieldData.input_type === "radio" ? (
          <Radio.Group
            {...commonProps}
            onChange={(e) => handleFormChange(e.target.value, fieldName)}
          >
            {(fieldData.option || []).map((option, i) => (
              <Radio key={i} value={option}>
                {option}
              </Radio>
            ))}
          </Radio.Group>
        ) : null}
      </Form.Item>
    );
  };

  const renderSection = (sectionName, sectionData) => {
    const fields = [];

    const renderFieldWithConditionals = (field) => {
      fields.push(renderField(field.name, field));

      const value = formData[field.id];
      if (
        field.conditional_fields &&
        field.conditional_fields[value] &&
        Array.isArray(field.conditional_fields[value])
      ) {
        field.conditional_fields[value].forEach((nestedField) => {
          renderFieldWithConditionals(nestedField);
        });
      }
    };

    sectionData.forEach((field) => {
      renderFieldWithConditionals(field);
    });

    return (
      <div key={sectionName} className="Reviewsection">
        <h3 className="section-title">{sectionName.replace(/_/g, " ")}</h3>

        {/* max 3 fields per row */}
        <Row gutter={[16, 16]}>
          {fields.map((field, idx) => (
            <Col span={8} key={idx}>
              {field}
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  const handleBackClick = () => {
    Modal.confirm({
      title: "Are you sure?",
      content: "If you go back now, all unsaved data will be lost.",
      okText: "Yes, Go Back",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk() {
        navigate("/clientList");
      },
    });
  };

  const handleViewAttachments = async () => {
    try {
      setloading(true);
      const client_id = sessionStorage.getItem("client_id");
      const res = await getClientAttachments(client_id);
      if (res.responseCode === 200) {
        setAttachments(res.attachments || []);
        setIsAttachmentsModalVisible(true);
      } else {
        message.error(res.responseMessage || "Failed to load attachments");
      }
    } catch (error) {
      console.error(error);
      message.error("Something went wrong loading attachments.");
    } finally {
      setloading(false);
    }
  };

  return (
    <>
      <Modal
        title="Upload Pricing Proof"
        open={isProofModalVisible}
        onCancel={() => setIsProofModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setIsProofModalVisible(false)}>
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            disabled={!proofFile} 
            onClick={() => handleUpdate()}
          >
            Submit Update
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <p>You have modified the client data. Please upload an approval/proof document (PDF/Image) to proceed.</p>
        </div>
        <div>
          <label><strong>Upload Proof Document <span style={{color: 'red'}}>*</span></strong></label>
          <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(e) => setProofFile(e.target.files[0])} />
        </div>
      </Modal>

      <Modal
        title="Client Attachments"
        open={isAttachmentsModalVisible}
        onCancel={() => setIsAttachmentsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsAttachmentsModalVisible(false)}>
            Close
          </Button>
        ]}
      >
        {attachments.length === 0 ? (
          <p>No attachments found for this client.</p>
        ) : (
          <ul style={{ listStyleType: "none", padding: 0 }}>
            {attachments.map((file, index) => (
              <li key={index} style={{ marginBottom: 10 }}>
                <strong>{file.type}: </strong> 
                <a href={file.url} target="_blank" rel="noreferrer">{file.name}</a>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      <div className="form-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <button className="back" onClick={() => handleBackClick()}>
            <AiOutlineArrowLeft />
          </button>
          <span style={{ fontWeight: 600 }}>Client Update Form</span>
        </div>
        <Button type="default" onClick={handleViewAttachments}>
          📎 View Attachments
        </Button>
      </div>
      <hr style={{ color: "#ECEEEB", margin: "0px" }} />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 1rem",
        }}
      >
        <h2 className="review-title">Review Client Information</h2>

        {hasPermission("client_list", "update") && (
          <Button
            type="primary"
            shape="round"
            onClick={() => setIsEditing(true)}
            style={{
              backgroundColor: "black",
              padding: "1.2rem",
            }}
          >
            Edit Information
          </Button>
        )}
      </div>

      <Spin
        spinning={loading}
        size="large"
        tip="Loading..."
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "76vh",
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdate}
          initialValues={formData}
          scrollToFirstError={{ behavior: "smooth", block: "center" }}
        >
          <div
            className="review-container custom-scrollbar"
            style={{ height: "62vh", marginTop: "1.5rem" }}
          >
            {Object.keys(formStructure).map((stepKey) => {
              const stepData = formStructure[stepKey];
              if (!stepData || !Array.isArray(stepData)) return null;
              return stepData.map((sectionData) => {
                const sectionName = Object.keys(sectionData)[0];
                return renderSection(sectionName, sectionData[sectionName]);
              });
            })}

            <ClientCountryFees clientId={sessionStorage.getItem("client_id")} />
            <AccountOwnersByCountry clientId={sessionStorage.getItem("client_id")} />
          </div>

          <div className="button-container">
            <Form.Item>
              <Button
                type="primary"
                shape="round"
                onClick={() => handleSave()}
                style={{
                  backgroundColor: "#4A3AFF",
                  padding: "1.2rem",
                }}
                disabled={!isEditing}
              >
                Submit Details
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Spin>
    </>
  );
};

export default UpdateClientForm;
